/**
 * LevelUpOverlay — Call-of-Duty-style level-up banner (Top-Center).
 *
 * Implements:
 * - Prominent top-center placement with minimal gap to the head navigation
 *   (Desktop & Mobile, z-index: 15000, guaranteed above all modals & gameplay).
 * - Instant audio cue via SoundEngine.playXpLevelUp().
 * - High-impact VFX ("reinhauen", "funkenregen", "kleiner lichtbogen") via LevelUpFX,
 *   supersonic kinetic slam-in, dual shockwave pulses, photon blast, and anamorphic lens flare.
 * - Starke Inszenierung der Level-Zahl und des freigeschalteten Unit-Sprites/Icons.
 * - Farbwelt: Crystal Blue with Gold Accents (#38bdf8, #0284c7, #fbbf24, #f59e0b).
 * - Puristisches, starkes Text-Layout: RANK UP Eyebrow, gewaltige Zahl, Rangtitel & Unlocked-Badge.
 */

import { getAvatarCatalog } from '../engine/avatarCatalog.js';
import { resolveAvatarImageUrl } from './avatarImage.js';
import { LevelUpFX } from './levelUpFX.js';

const VISIBLE_MS = 3800;
const HIDE_TRANSITION_MS = 450;

export class LevelUpOverlay {
  constructor(audio = null, container = document.body) {
    this.audio = audio;
    this.container = container;
    this.el = null;
    this.fx = null;
    this._hideTimer = null;
    this._queue = [];
    this._initElements();
  }

  _initElements() {
    let el = document.getElementById('level-up-overlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'level-up-overlay';
      el.className = 'level-up-overlay hidden';
      this.container.appendChild(el);
    }
    this.el = el;
  }

  /**
   * Shows a level-up banner.
   * @param {number} newLevel - The newly reached level number
   * @param {string} title - The rank title (e.g. "TACTICAL OPERATOR")
   * @param {Object|null} unlockedAvatar - Optional avatar definition from avatarCatalog
   */
  show(newLevel, title, unlockedAvatar = null) {
    if (!this.el) return;
    const wasIdle = !this._hideTimer && this._queue.length === 0;
    this._queue.push({ newLevel, title, unlockedAvatar });
    if (wasIdle) {
      this._render(this._queue[0], true);
    }
  }

  _render({ newLevel, title, unlockedAvatar }, playSound = false) {
    if (!this.el) return;

    // Resolve unlocked avatar sprite / artwork for this level
    let avatar = unlockedAvatar;
    if (!avatar && typeof newLevel === 'number') {
      try {
        const catalog = getAvatarCatalog();
        avatar = catalog.find(a => a.unlockLevel === newLevel) || null;
      } catch (err) {
        console.warn('[LevelUpOverlay] Could not query avatar catalog:', err);
      }
    }

    let iconUrl = null;
    if (avatar) {
      try {
        iconUrl = resolveAvatarImageUrl(avatar);
      } catch (err) {
        console.warn('[LevelUpOverlay] Could not resolve avatar image URL:', err);
      }
    }

    const rewardText = avatar ? `UNLOCKED: ${avatar.unitName.toUpperCase()}` : 'PROMOTION AUTHORIZED';

    // SVG military crest fallback if no unit sprite is found
    const crestFallbackSvg = `
      <svg class="level-up-crest-svg" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 4L6 18L10 22L24 11L38 22L42 18L24 4Z" fill="#fbbf24" />
        <path d="M24 16L12 26L16 30L24 23L32 30L36 26L24 16Z" fill="#38bdf8" />
        <path d="M24 28L18 34L22 38L24 36L26 38L30 34L24 28Z" fill="#7dd3fc" />
      </svg>
    `;

    // Dynamic positioning with minimal gap (8px) directly under active head nav
    if (typeof document !== 'undefined') {
      try {
        const activeHeader = document.querySelector(
          '.deck-select-modal-container.active .deckbuilder-header, #deckbuilder-modal.active .deckbuilder-header, .station-transit-rail:not([style*="display: none"]), .deckbuilder-header'
        );
        if (activeHeader && typeof activeHeader.getBoundingClientRect === 'function') {
          const rect = activeHeader.getBoundingClientRect();
          if (rect && rect.bottom > 0) {
            this.el.style.top = `${Math.round(rect.bottom + 8)}px`;
          }
        } else {
          const topHud = document.getElementById('top-hud');
          if (topHud && typeof topHud.getBoundingClientRect === 'function') {
            const rect = topHud.getBoundingClientRect();
            if (rect && rect.bottom > 0) {
              this.el.style.top = `${Math.round(rect.bottom + 8)}px`;
            }
          }
        }
      } catch (e) {
        // Fallback gracefully to CSS top
      }
    }

    this.el.innerHTML = `
      <div class="level-up-impact-blast"></div>
      <div class="level-up-shockwave"></div>
      <div class="level-up-shockwave-outer"></div>
      <div class="level-up-lens-flare"></div>
      <canvas class="level-up-fx-canvas" width="640" height="420"></canvas>
      <div class="level-up-badge">
        <div class="level-up-ambient-glow"></div>
        <div class="level-up-header-row">
          <span class="level-up-laser-line left"></span>
          <span class="level-up-eyebrow">RANK UP</span>
          <span class="level-up-laser-line right"></span>
        </div>
        <div class="level-up-emblem-wrap">
          <div class="level-up-icon-capsule ${iconUrl ? 'has-sprite' : 'has-crest'}">
            ${iconUrl ? `<img src="${iconUrl}" alt="Unlocked Unit" class="level-up-sprite-img">` : crestFallbackSvg}
            <div class="level-up-capsule-glint"></div>
          </div>
        </div>
        <div class="level-up-hero-block">
          <div class="level-up-num-row">
            <span class="level-up-prefix">LEVEL</span>
            <span class="level-up-level-val">${newLevel}</span>
          </div>
          <div class="level-up-title-val">${title || 'COMMANDER'}</div>
        </div>
        <div class="level-up-reward-chip">
          <span class="level-up-reward-star">★</span>
          <span class="level-up-reward-text">${rewardText}</span>
        </div>
      </div>
    `;

    // File-based XP/level-up stinger (assets/music/short/xp_level_up.mp3)
    if (playSound) {
      try {
        this.audio?.playXpLevelUp?.();
      } catch (err) {
        console.error('[LevelUpOverlay] audio failed:', err);
      }
    }

    // Launch LevelUpFX (sparks + electric arcs + core blast)
    try {
      const canvas = this.el.querySelector('.level-up-fx-canvas');
      if (canvas) {
        this.fx = new LevelUpFX(canvas);
        const capsule = this.el.querySelector('.level-up-icon-capsule');
        this.fx.start(capsule);
      }
    } catch (err) {
      console.warn('[LevelUpOverlay] FX failed to start:', err);
    }

    this.el.classList.remove('hidden');
    this.el.classList.remove('level-up-in', 'level-up-out');
    void this.el.offsetWidth; // force reflow
    this.el.classList.add('level-up-in');

    this._hideTimer = setTimeout(() => this._hide(), VISIBLE_MS);
  }

  _hide() {
    this._hideTimer = null;
    if (this.fx) {
      this.fx.stop();
    }
    if (!this.el) return;
    this.el.classList.remove('level-up-in');
    this.el.classList.add('level-up-out');

    setTimeout(() => {
      if (!this.el) return;
      this.el.classList.add('hidden');
      this.el.classList.remove('level-up-out');
      this._queue.shift();
      if (this._queue.length > 0) {
        // Render subsequent queued level-ups
        this._render(this._queue[0], false);
      }
    }, HIDE_TRANSITION_MS);
  }

  reset() {
    if (this._hideTimer) {
      clearTimeout(this._hideTimer);
      this._hideTimer = null;
    }
    if (this.fx) {
      this.fx.stop();
    }
    this._queue = [];
    if (this.el) {
      this.el.classList.add('hidden');
      this.el.classList.remove('level-up-in', 'level-up-out');
    }
  }
}
